<template>
  <div class="entity-list-page">
    <div class="entity-list-page__header">
      <title-vue title="Consumidores" />
    </div>

    <v-skeleton-loader
      v-if="loading"
      tile
      loading
      class="mx-auto"
      type="card"
      boilerplate
    />

    <v-card v-else class="entity-list-page__card">
      <v-card-text class="pb-0">
        <status-filter-chips
          :value="statusFiltered"
          :options="statusOptions"
          @input="onStatusChange"
        />
      </v-card-text>

      <v-card-title class="pt-4">
        <v-row align="center">
          <v-col cols="12" md="8">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Pesquise pelo nome, CPF, email ou telefone"
              hide-details
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              clearable
              dense
              outlined
              hide-details
              :items="franchises"
              v-model="franchiseFiltered"
              item-text="cityName"
              item-value="ibge"
              label="Franquia"
              @change="handleLoadCustomers"
            />
          </v-col>
        </v-row>
      </v-card-title>

      <v-data-table
        :header-props="{ sortByText: 'Filtrar por' }"
        :headers="headers"
        :items="customers"
        :items-per-page="10"
        :footer-props="{ 'items-per-page-text': 'consumidores por página' }"
        :search="search"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        no-data-text="Nenhum consumidor encontrado"
        class="elevation-0"
      >
        <template v-slot:body="{ items }">
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>{{ item.name }}</td>
              <td>{{ formatDocument(item.document) }}</td>
              <td>{{ phoneNumberFormat(item.phone) }}</td>
              <td>{{ item.email }}</td>
              <td>{{ item.cityName || "—" }}</td>
              <td>{{ formatDate(item.createdAt) }}</td>
              <td class="text-center">
                <v-icon size="24" color="#e5183d" @click="handleDetailCustomer(item.id)">
                  mdi-chevron-right
                </v-icon>
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import TitleVue from "@/components/title/Title.vue";
import StatusFilterChips from "@/components/statusFilter/StatusFilterChips.vue";
import { CUSTOMER_STATUS_FILTERS } from "@/config/userStatusFilters";
import { OnboardingType } from "@/types/onboarding/Onboarding";
import { FranchiseType } from "@/types/franchise/Franchise";
import { GetAllOnbordings } from "@/services/onboardings/GetAllOnboardings";
import { GetAllFranchises } from "@/services/franchises/GetAllFranchises";
import { format } from "date-fns";

export default defineComponent({
  components: { TitleVue, StatusFilterChips },

  data() {
    return {
      loading: true,
      customers: [] as OnboardingType[],
      franchises: [] as FranchiseType[],
      search: "",
      sortBy: "createdAt",
      sortDesc: true,
      statusFiltered: "WAITING",
      franchiseFiltered: undefined as number | undefined,
      statusOptions: CUSTOMER_STATUS_FILTERS,
      headers: [
        { text: "Nome", value: "name" },
        { text: "CPF", value: "document" },
        { text: "Telefone", value: "phone" },
        { text: "Email", value: "email" },
        { text: "Cidade", value: "cityName" },
        { text: "Cadastro", value: "createdAt" },
        { text: "", value: "actions", sortable: false, width: "48px" },
      ],
    };
  },

  watch: {
    "$route.query.status"(status: string | undefined) {
      if (status && status !== this.statusFiltered) {
        this.statusFiltered = status;
        void this.handleLoadCustomers();
      }
    },
  },

  methods: {
    onStatusChange(status: string) {
      this.statusFiltered = status;
      this.$router.replace({ query: { ...this.$route.query, status } }).catch(() => undefined);
      void this.handleLoadCustomers();
    },
    async handleLoadFranchises() {
      const getAllFranchises = new GetAllFranchises();
      this.franchises = await getAllFranchises.execute();
    },
    async handleLoadCustomers() {
      this.loading = true;
      try {
        const getAllCustomers = new GetAllOnbordings();
        this.customers = await getAllCustomers.execute(
          this.statusFiltered,
          this.franchiseFiltered,
        );
      } finally {
        this.loading = false;
      }
    },
    handleDetailCustomer(id: string) {
      this.$router.push({
        path: `/customer/detail/${id}`,
        query: { status: this.statusFiltered },
      });
    },
    phoneNumberFormat(value: string): string {
      if (!value) return "—";
      return `(${value.substr(0, 2)}) ${value.substr(2, 5)}-${value.substr(7, 8)}`;
    },
    formatDate(date: string): string {
      if (!date) return "—";
      return format(new Date(date), "dd/MM/yyyy HH:mm");
    },
    formatDocument(document: string): string {
      if (!document) return "—";
      if (document.length === 11) {
        return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      if (document.length === 14) {
        return document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      }
      return document;
    },
  },

  async mounted() {
    const queryStatus = this.$route.query.status;
    if (typeof queryStatus === "string" && queryStatus.length > 0) {
      this.statusFiltered = queryStatus;
    }
    await this.handleLoadFranchises();
    await this.handleLoadCustomers();
  },
});
</script>

<style scoped lang="scss">
.entity-list-page__header {
  margin-bottom: 20px;
}

.entity-list-page__card {
  overflow: hidden;
}

tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.03);
}
</style>
